import {
  BASE_W,
  BATTERY_R,
  GROUND_H,
  MULT_MAX,
  MULT_TIME,
  SUN_R,
  SUN_X,
  CloudBand,
  GameState,
  LevelTheme,
  metersToFeet,
} from '../engine';

// Deterministic per-cloud jitter from a seed (matches the prototype look).
const hash = (seed: number, k: number): number => {
  const v = Math.sin(seed * 12.9898 + k * 78.233) * 43758.5453;
  return v - Math.floor(v);
};

const drawDecor = (ctx: CanvasRenderingContext2D, state: GameState, lvl: LevelTheme): void => {
  const { h, w } = state;
  if (lvl.decor === 'stars') {
    ctx.fillStyle = '#FFFFFF';
    state.stars.forEach((s) => {
      ctx.globalAlpha = 0.5 + ((Math.sin(state.bob * 2 + s.x) + 1) / 2) * 0.5;
      ctx.fillRect(s.x, s.y, s.r, s.r);
    });
    ctx.globalAlpha = 1;
  } else if (lvl.decor === 'rain') {
    ctx.strokeStyle = 'rgba(200,220,235,0.35)';
    ctx.lineWidth = 1;
    const off = (state.distance * 6) % 24;
    for (let x = -24; x < w; x += 24) {
      for (let y = -24; y < h - GROUND_H; y += 24) {
        ctx.beginPath();
        ctx.moveTo(x + off, y + off);
        ctx.lineTo(x + off - 5, y + off + 10);
        ctx.stroke();
      }
    }
  } else if (lvl.decor === 'dust') {
    ctx.fillStyle = 'rgba(120,90,50,0.18)';
    const off = (state.distance * 4) % 18;
    for (let x = -18; x < w; x += 18) {
      for (let y = 0; y < h - GROUND_H; y += 18) {
        ctx.fillRect((x + off) % w, (y + off * 0.5) % (h - GROUND_H), 2, 2);
      }
    }
  }
};

const drawCloud = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  baseY: number,
  cw: number,
  hgt: number,
  seed: number,
  lvl: LevelTheme
): void => {
  const left = cx - cw / 2;
  const cyc = baseY - hgt * 0.5;
  const a = cw * 0.42;
  const b = hgt * 0.55;

  type Bump = { r: number; x: number; y: number };
  const bumps: Bump[] = [];
  const k = 4 + Math.floor(hash(seed, 1) * 4);
  for (let i = 0; i < k; i += 1) {
    const f = (i + 0.5) / k;
    const ang = Math.PI * 0.86 + f * Math.PI * 1.28 + (hash(seed, i * 5 + 9) - 0.5) * 0.3;
    const topFactor = 0.5 - 0.5 * Math.sin(ang);
    const br = Math.min(cw, hgt * 2) * 0.27 * (0.65 + 0.6 * topFactor) * (0.82 + hash(seed, i * 5 + 3) * 0.5);
    bumps.push({ r: Math.max(6, br), x: cx + Math.cos(ang) * a, y: cyc + Math.sin(ang) * b });
  }

  const path = (dx: number, dy: number) => {
    ctx.beginPath();
    ctx.rect(left + dx, cyc + dy, cw, baseY - cyc);
    bumps.forEach((bp) => {
      ctx.moveTo(bp.x + bp.r + dx, bp.y + dy);
      ctx.arc(bp.x + dx, bp.y + dy, bp.r, 0, Math.PI * 2);
    });
  };

  ctx.fillStyle = 'rgba(20,28,40,0.16)';
  path(3, 5);
  ctx.fill();

  ctx.save();
  ctx.beginPath();
  ctx.rect(left - cw, cyc - hgt * 2, cw * 3, baseY - (cyc - hgt * 2));
  ctx.clip();
  ctx.fillStyle = lvl.cloud;
  path(0, 0);
  ctx.fill();

  path(0, 0);
  ctx.clip();
  ctx.globalAlpha = 0.55;
  ctx.fillStyle = lvl.cloudShade;
  ctx.fillRect(left - cw, baseY - Math.max(5, hgt * 0.28), cw * 3, hgt);
  ctx.globalAlpha = 1;
  ctx.strokeStyle = lvl.cloudShade;
  ctx.lineWidth = 1;
  bumps.forEach((bp, i) => {
    if (hash(seed, i * 9 + 5) < 0.45) return;
    ctx.beginPath();
    ctx.arc(bp.x, bp.y, Math.max(4, bp.r * 0.8), Math.PI * 0.12, Math.PI * 0.88);
    ctx.stroke();
  });
  ctx.restore();
};

const drawCloudBand = (ctx: CanvasRenderingContext2D, x: number, band: CloudBand, lvl: LevelTheme): void => {
  const h = band.bot - band.top;
  if (h <= 0) return;
  const w0 = band.w;
  type Spec = { baseY: number; cx: number; hgt: number; seed: number; w: number };
  const specs: Spec[] = [];
  let y = band.bot;
  for (let i = 0; i < 16 && y > band.top + 6; i += 1) {
    let hgt = Math.min(w0 * 0.46, h) * (0.68 + hash(band.seed, i * 7 + 1) * 0.6);
    hgt = Math.max(14, hgt);
    const w = Math.max(hgt * 1.7, w0 * (0.78 + hash(band.seed, i * 7 + 2) * 0.5));
    const cx = x + w0 / 2 + (hash(band.seed, i * 7 + 3) - 0.5) * w0 * 0.38;
    specs.push({ baseY: y, cx, hgt, seed: band.seed + i * 1.913, w });
    y -= hgt * (0.5 + hash(band.seed, i * 7 + 4) * 0.28);
  }
  for (let k = specs.length - 1; k >= 0; k -= 1) {
    const s = specs[k];
    drawCloud(ctx, s.cx, s.baseY, s.w, s.hgt, s.seed, lvl);
  }
};

const drawBatteries = (ctx: CanvasRenderingContext2D, state: GameState): void => {
  state.batteries.forEach((batt) => {
    if (batt.collected) return;
    const y = batt.y + Math.sin(state.bob * 3 + batt.bob) * 4;
    ctx.save();
    ctx.translate(batt.x, y);
    const pulse = 0.55 + ((Math.sin(state.bob * 5 + batt.bob) + 1) / 2) * 0.45;
    const glow = ctx.createRadialGradient(0, 0, 2, 0, 0, BATTERY_R + 9);
    glow.addColorStop(0, `rgba(80,225,150,${0.45 * pulse})`);
    glow.addColorStop(1, 'rgba(80,225,150,0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(0, 0, BATTERY_R + 9, 0, Math.PI * 2);
    ctx.fill();
    const bw = 12;
    const bh = 16;
    ctx.fillStyle = '#0E3D2B';
    ctx.fillRect(-bw / 2 - 1, -bh / 2 - 1, bw + 2, bh + 2);
    ctx.fillStyle = '#27E08A';
    ctx.fillRect(-bw / 2, -bh / 2, bw, bh);
    ctx.fillStyle = '#0E3D2B';
    ctx.fillRect(-3, -bh / 2 - 3, 6, 3);
    ctx.beginPath();
    ctx.moveTo(1, -5);
    ctx.lineTo(-3, 1);
    ctx.lineTo(0, 1);
    ctx.lineTo(-1, 5);
    ctx.lineTo(3, -1);
    ctx.lineTo(0, -1);
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = pulse;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(BATTERY_R - 1, -BATTERY_R, 2, 2);
    ctx.restore();
  });
};

const drawPanels = (ctx: CanvasRenderingContext2D, state: GameState, lvl: LevelTheme): void => {
  const { h, w } = state;
  const y = h - GROUND_H;
  ctx.fillStyle = '#0E1B3A';
  ctx.fillRect(0, y, w, GROUND_H);
  const intensity = Math.min(1, lvl.powerKw / 7.6);
  ctx.fillStyle = `rgba(255,210,80,${0.08 + intensity * 0.18})`;
  ctx.fillRect(0, y, w, 5);
  ctx.strokeStyle = '#1E3A6E';
  ctx.lineWidth = 1;
  const cell = 16;
  for (let px = 0; px <= w; px += cell) {
    ctx.beginPath();
    ctx.moveTo(px, y + 6);
    ctx.lineTo(px, h);
    ctx.stroke();
  }
  for (let py = y + 6; py <= h; py += cell) {
    ctx.beginPath();
    ctx.moveTo(0, py);
    ctx.lineTo(w, py);
    ctx.stroke();
  }
  ctx.fillStyle = `rgba(120,180,255,${0.15 + intensity * 0.35})`;
  for (let px = 2; px < w; px += cell) {
    if ((Math.floor(px / cell) + Math.floor(state.distance / 6)) % 3 === 0) {
      ctx.fillRect(px, y + 8, cell - 4, cell - 4);
    }
  }
};

const drawParticles = (ctx: CanvasRenderingContext2D, state: GameState): void => {
  state.particles.forEach((p) => {
    ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
    ctx.fillStyle = p.color;
    ctx.fillRect(p.x, p.y, 2, 2);
  });
  ctx.globalAlpha = 1;
};

const drawSun = (ctx: CanvasRenderingContext2D, state: GameState): void => {
  const x = SUN_X;
  const y = state.sunY;
  const tilt = state.status === 'running' ? Math.max(-0.5, Math.min(0.5, state.vy / 500)) : 0;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(state.bob);
  const combo = state.comboMult > 1 && state.status === 'running';
  ctx.strokeStyle = combo ? (state.comboMult >= MULT_MAX ? '#27E08A' : '#9BE86A') : '#FFC83D';
  ctx.lineWidth = combo ? 3 + ((Math.sin(state.bob * 10) + 1) / 2) * 1.5 : 3;
  for (let i = 0; i < 8; i += 1) {
    const a = (i / 8) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * (SUN_R + 3), Math.sin(a) * (SUN_R + 3));
    ctx.lineTo(Math.cos(a) * (SUN_R + 9), Math.sin(a) * (SUN_R + 9));
    ctx.stroke();
  }
  ctx.restore();
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(tilt);
  const g = ctx.createRadialGradient(-3, -3, 2, 0, 0, SUN_R);
  g.addColorStop(0, '#FFF3B0');
  g.addColorStop(1, '#FDB813');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, SUN_R, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#1E1402';
  ctx.fillRect(-5, -3, 2, 3);
  ctx.fillRect(3, -3, 2, 3);
  ctx.beginPath();
  ctx.arc(0, 3, 4, 0, Math.PI);
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = '#1E1402';
  ctx.stroke();
  ctx.restore();
};

const drawPops = (ctx: CanvasRenderingContext2D, state: GameState): void => {
  ctx.save();
  ctx.textAlign = 'center';
  ctx.font = '9px monospace';
  state.pops.forEach((p) => {
    ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
    ctx.fillStyle = '#27E08A';
    ctx.fillText(p.text, p.x, p.y);
  });
  ctx.restore();
  ctx.textAlign = 'left';
};

const drawHud = (ctx: CanvasRenderingContext2D, state: GameState): void => {
  const { h, w } = state;
  if (state.status !== 'idle' && state.status !== 'ready') {
    const mobile = w < BASE_W;
    const kwhW = mobile ? 92 : 150;
    const kwhY = 8;
    const distW = mobile ? 78 : 112;
    const battW = mobile ? 122 : 150;
    const battY = 34;

    ctx.textBaseline = 'top';
    ctx.font = '10px monospace';
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillRect(8, kwhY, kwhW, 22);
    ctx.fillStyle = '#FFE9A8';
    ctx.fillText(`${state.energy.toFixed(1)} kWh`, 14, kwhY + 6);
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillRect(w - distW - 8, 8, distW, 22);
    ctx.fillStyle = '#CFE8FF';
    ctx.textAlign = 'right';
    ctx.fillText(`${Math.floor(metersToFeet(state.distance))} ft`, w - 14, 14);
    ctx.textAlign = 'left';

    if (state.batteriesCollected > 0) {
      ctx.fillStyle = 'rgba(0,0,0,0.45)';
      ctx.fillRect(8, battY, battW, 20);
      ctx.fillStyle = '#27E08A';
      ctx.fillRect(14, battY + 5, 9, 10);
      ctx.fillRect(23, battY + 7, 2, 6);
      ctx.fillStyle = '#9CF5C9';
      ctx.font = '9px monospace';
      ctx.fillText(`x${state.batteriesCollected}  +${state.storageKwh.toFixed(0)} kWh`, 30, battY + 7);
    }

    if (state.comboMult > 1) {
      const maxed = state.comboMult >= MULT_MAX;
      const bw = maxed ? 128 : 96;
      const bx = (w - bw) / 2;
      const pulse = 0.7 + ((Math.sin(state.bob * 10) + 1) / 2) * 0.3;
      ctx.save();
      ctx.globalAlpha = pulse;
      ctx.fillStyle = maxed ? 'rgba(22,92,44,0.92)' : 'rgba(14,61,43,0.85)';
      ctx.fillRect(bx, 8, bw, 24);
      ctx.fillStyle = maxed ? '#FFE066' : '#27E08A';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.font = maxed ? 'bold 13px monospace' : '14px monospace';
      ctx.fillText(maxed ? '★ SOLAR SURGE ★' : `x${state.comboMult} OUTPUT`, w / 2, 12);
      const frac = Math.max(0, state.multTimer / MULT_TIME);
      ctx.globalAlpha = 1;
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.fillRect(bx, 28, bw, 3);
      ctx.fillStyle = maxed ? '#FFE066' : '#9CF5C9';
      ctx.fillRect(bx, 28, bw * frac, 3);
      ctx.restore();
      ctx.textAlign = 'left';
    }

    ctx.save();
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    ctx.font = '8px monospace';
    ctx.fillStyle = '#000000';
    ctx.fillText(state.level.concept.toUpperCase(), w - 10, 36);
    ctx.restore();
    ctx.textAlign = 'left';

    if (state.milestoneFlash > 0) {
      ctx.save();
      ctx.globalAlpha = Math.min(1, state.milestoneFlash);
      ctx.fillStyle = '#FFD23F';
      ctx.font = 'bold 18px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(state.milestoneText, w / 2, h * 0.3);
      ctx.restore();
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
    }
  }

  if (state.flash > 0) {
    ctx.globalAlpha = Math.min(1, state.flash);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(0, h / 2 - 30, w, 60);
    ctx.fillStyle = '#FFD23F';
    ctx.font = '16px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(`LEVEL ${state.flashLevel.index}  ·  ${state.flashLevel.title}`, w / 2, h / 2 - 14);
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '11px monospace';
    ctx.fillText(state.flashLevel.concept.toUpperCase(), w / 2, h / 2 + 6);
    if (state.flashLevel.index === 4) {
      ctx.fillStyle = '#27E08A';
      ctx.font = '9px monospace';
      ctx.fillText('HALFWAY, 3 CONCEPTS UNLOCKED', w / 2, h / 2 + 22);
    }
    ctx.textAlign = 'left';
    ctx.globalAlpha = 1;
  }

  if (state.grace > 0 && state.status === 'running') {
    ctx.save();
    ctx.globalAlpha = 0.6 + ((Math.sin(state.bob * 8) + 1) / 2) * 0.4;
    ctx.fillStyle = '#000000';
    ctx.font = '11px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('GET READY!', SUN_X, state.sunY - SUN_R - 12);
    ctx.textAlign = 'left';
    ctx.restore();
  }
};

/** Paint one frame of the simulation. Assumes the context is already scaled to
 *  the device pixel ratio and that ctx reads state.w / state.h logical units. */
export const draw = (ctx: CanvasRenderingContext2D, state: GameState): void => {
  const { h, w } = state;
  const lvl = state.level;

  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, lvl.sky[0]);
  grad.addColorStop(1, lvl.sky[1]);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  drawDecor(ctx, state, lvl);

  if (state.status !== 'over') {
    const intensity = Math.min(1, lvl.powerKw / 7.6);
    ctx.save();
    ctx.globalAlpha = 0.12 + intensity * 0.25;
    const beam = ctx.createLinearGradient(0, state.sunY, 0, h - GROUND_H);
    beam.addColorStop(0, '#FFF2B0');
    beam.addColorStop(1, 'rgba(255,242,176,0)');
    ctx.fillStyle = beam;
    ctx.beginPath();
    ctx.moveTo(SUN_X - SUN_R, state.sunY);
    ctx.lineTo(SUN_X + SUN_R, state.sunY);
    ctx.lineTo(SUN_X + SUN_R * 3.2, h - GROUND_H);
    ctx.lineTo(SUN_X - SUN_R * 3.2, h - GROUND_H);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  state.obstacles.forEach((o) => o.bands.forEach((band) => drawCloudBand(ctx, o.x, band, lvl)));
  drawBatteries(ctx, state);
  drawPanels(ctx, state, lvl);
  drawParticles(ctx, state);
  drawSun(ctx, state);
  drawPops(ctx, state);
  drawHud(ctx, state);
};
